import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import { TextField, Autocomplete, CircularProgress, debounce, Box, Typography } from '@mui/material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icon missing in Leaflet + React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Helper component to update map view when search result is selected
function RecenterMap({ coords }) {
  const map = useMap();
  useEffect(() => {
    if (coords) {
      map.setView([coords.lat, coords.lng], 16);
    }
  }, [coords, map]);
  return null;
}

const LocationPicker = ({ onLocationChange }) => {
  const [position, setPosition] = useState({ lat: 51.505, lng: -0.09 }); // Default
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);

  // 1. Search Logic (Same as before)
  const fetchArea = useMemo(
    () => debounce(async (query, callback) => {
      if (query.length < 3) return;
      setLoading(true);
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=5`);
        const data = await res.json();
        callback(data);
      } finally { setLoading(false); }
    }, 500), []
  );

  useEffect(() => { fetchArea(inputValue, setOptions); }, [inputValue, fetchArea]);

  // 2. Map Interaction: Update pin when user clicks on map
  const MapEvents = () => {
    useMapEvents({
      click(e) {
        const newPos = { lat: e.latlng.lat, lng: e.latlng.lng };
        setPosition(newPos);
        onLocationChange(newPos);
      },
    });
    return null;
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}>
      {/* Search Bar */}
      <Autocomplete
        fullWidth
        options={options}
        getOptionLabel={(o) => o.display_name || ""}
        onInputChange={(e, val) => setInputValue(val)}
        onChange={(e, val) => {
          if (val) {
            const newPos = { lat: parseFloat(val.lat), lng: parseFloat(val.lon) };
            setPosition(newPos);
            onLocationChange(newPos);
          }
        }}
        renderInput={(params) => (
          <TextField {...params} label="Search Area to Update Map" variant="filled" sx={{ mb: 1 }}
            InputProps={{ ...params.InputProps, endAdornment: ( <>{loading ? <CircularProgress size={20} /> : null}{params.InputProps.endAdornment}</> ) }}
          />
        )}
      />

      {/* Map Display */}
      <Box sx={{ height: 350, width: '100%', borderRadius: 2, overflow: 'hidden', border: '1px solid #ccc' }}>
        <MapContainer center={[position.lat, position.lng]} zoom={13} style={{ height: '100%', width: '100%' }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <RecenterMap coords={position} />
          <MapEvents />
          <Marker position={[position.lat, position.lng]} draggable={true}
            eventHandlers={{
              dragend: (e) => {
                const newPos = e.target.getLatLng();
                setPosition(newPos);
                onLocationChange(newPos);
              }
            }}
          />
        </MapContainer>
      </Box>
      <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
        Search for an area, then click or drag the pin to set the exact office location.
      </Typography>
    </Box>
  );
};

export default LocationPicker;